import React, { useCallback, useEffect, useRef, useState } from "react";
import * as Scrivito from "scrivito";
import cx from "classnames";
import { Input, Tooltip } from "reactstrap";
import { useClickOutside, useTranslate } from "hooks";
import s from "../WizardComponent.module.scss";

const isStringOnlyWhitespace = (str) => /^\s*$/.test(str);

export const TopologyEditableTitle = Scrivito.connect(
  ({
    children,
    title,
    zoneId,
    maxLength = 30,
    handleUpdateTitle,
    handleEditMode,
    editMode = false,
    className,
    inputClassNames,
  }) => {
    const translate = useTranslate();
    const inputRef = useRef(null);
    const [inputValue, setInputValue] = useState(title || "");
    const [showTooltip, setShowTooltip] = useState(false);

    const handleToggleEditMode = useCallback(() => {
      handleEditMode((val) => !val);
    }, [handleEditMode]);

    const handleInputChange = useCallback((e) => {
      setInputValue(e.target.value);
      setShowTooltip(false);
    }, []);

    const onUpdateWizard = useCallback(() => {
      if (inputValue !== title) {
        handleUpdateTitle(inputValue, zoneId);
      }
      handleEditMode(false);
    }, [inputValue, title, handleUpdateTitle, zoneId, handleEditMode]);

    const handleClickOutside = useCallback(() => {
      if (inputValue.length < 4 || isStringOnlyWhitespace(inputValue)) {
        setShowTooltip(true);
        return;
      }
      if (editMode) {
        onUpdateWizard();
        handleEditMode(false);
      }
    }, [inputValue, editMode, onUpdateWizard, handleEditMode]);

    useClickOutside(
      inputRef,
      () => {
        handleClickOutside();
      },
      editMode,
    );

    const handleInputKeyDown = useCallback(
      (e) => {
        if (e.key === "Enter") {
          handleClickOutside();
        }
      },
      [handleClickOutside],
    );

    useEffect(() => {
      if (editMode && inputRef.current) {
        inputRef.current.focus();
      }
    }, [editMode]);

    useEffect(() => {
      if (!editMode) {
        setInputValue(title || "");
      }
    }, [title, editMode]);

    return (
      <div className={className}>
        {editMode ? (
          <div className="position-relative d-flex align-items-center">
            <Input
              innerRef={inputRef}
              className={cx(inputClassNames, { "pe-7": maxLength })}
              type="text"
              bsSize="sm"
              value={inputValue}
              onChange={handleInputChange}
              maxLength={maxLength}
              onKeyDown={handleInputKeyDown}
            />
            <Tooltip
              isOpen={showTooltip}
              target={() => inputRef.current}
              placement="right"
            >
              {translate("WIZARD_ZONE_NAME_LENGTH_HINT")}
            </Tooltip>
            <div
              className={cx(
                s.inputLengthLabel,
                "text-gray-400 d-flex flex-nowrap",
              )}
            >
              <span className="fw-bolder">{inputValue.length}</span>
              <span>&nbsp;/ {maxLength}</span>
            </div>
          </div>
        ) : (
          <div onClick={handleToggleEditMode}>{children}</div>
        )}
      </div>
    );
  },
);
